import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckCircle2,
  CircleAlert,
  FileImage,
  Loader2,
  LockKeyhole,
  Maximize2,
  RefreshCw,
  ShieldCheck,
  UploadCloud,
  UserRound,
  X,
} from 'lucide-react';
import SessionReauthDialog from './SessionReauthDialog';
import {
  getPersonalCertificationDocumentPreview,
  submitPersonalCertification,
  uploadPersonalCertificationDocument,
} from './corporateFinanceApi';

const MAX_DOCUMENT_SIZE = 10 * 1024 * 1024;
const PREVIEW_LOAD_TIMEOUT_MS = 15000;
const DOCUMENT_TYPES = new Set(['image/jpeg', 'image/png']);
const ID_CARD_PATTERN = /^[1-9]\d{16}[0-9X]$/;
const ID_CARD_WEIGHTS = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
const ID_CARD_CHECK_CODES = ['1', '0', 'X', '9', '8', '7', '6', '5', '4', '3', '2'];
const NUMERIC_STATUS = {
  0: 'pending',
  1: 'approved',
  2: 'rejected',
  3: 'revoked',
  4: 'superseded',
};

const emptyForm = (certification, user) => ({
  real_name: certification?.real_name || user?.real_name || '',
  id_card_number: '',
  information_confirmed: false,
});

export const normalizePersonalCertificationStatus = certification => {
  if (!certification) return 'none';
  return NUMERIC_STATUS[certification.status]
    || String(certification.status || 'none').toLowerCase();
};

export const isValidIdCardNumber = value => {
  const normalized = String(value || '').trim().toUpperCase();
  if (!ID_CARD_PATTERN.test(normalized)) return false;
  const birthDate = normalized.slice(6, 14);
  const year = Number(birthDate.slice(0, 4));
  const month = Number(birthDate.slice(4, 6));
  const day = Number(birthDate.slice(6, 8));
  const parsed = new Date(Date.UTC(year, month - 1, day));
  if (parsed.getUTCFullYear() !== year || parsed.getUTCMonth() !== month - 1 || parsed.getUTCDate() !== day) return false;
  if (parsed.getTime() > Date.now()) return false;
  const checksum = normalized.slice(0, 17)
    .split('')
    .reduce((total, digit, index) => total + Number(digit) * ID_CARD_WEIGHTS[index], 0);
  return ID_CARD_CHECK_CODES[checksum % 11] === normalized.at(-1);
};

const dateText = value => value ? new Date(value).toLocaleString('zh-CN') : '—';

function statusTone(status) {
  if (status === 'approved') return 'success';
  if (status === 'pending') return 'pending';
  if (['rejected', 'revoked'].includes(status)) return 'danger';
  return 'neutral';
}

function documentError(file) {
  if (!DOCUMENT_TYPES.has(file?.type)) return '身份证照片仅支持 JPG 或 PNG 格式';
  if (file.size > MAX_DOCUMENT_SIZE) return '单张照片大小不能超过 10 MB';
  return '';
}

function certificationDocument(certification, documentType) {
  const existing = Array.isArray(certification?.documents)
    ? certification.documents.find(item => item.document_type === documentType)
    : null;
  if (existing) return existing;
  const documentId = certification?.[`${documentType}_document_id`];
  return documentId ? { id: documentId, document_type: documentType } : null;
}

function PersonalDocumentPreview({ preview, onClose, onRetry, darkActive }) {
  const closeButtonRef = useRef(null);
  const [contentState, setContentState] = useState('loading');
  const { material, label, returnFocus } = preview;

  useEffect(() => {
    if (contentState !== 'loading') return undefined;
    const timeoutId = window.setTimeout(() => setContentState('error'), PREVIEW_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timeoutId);
  }, [contentState, material.preview_url]);

  useEffect(() => {
    closeButtonRef.current?.focus();
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    const handleDialogKeys = event => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const dialog = closeButtonRef.current?.closest('[role="dialog"]');
      const focusable = Array.from(dialog?.querySelectorAll('button:not(:disabled)') || []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && window.document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && window.document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.document.addEventListener('keydown', handleDialogKeys);
    return () => {
      window.document.removeEventListener('keydown', handleDialogKeys);
      window.document.body.style.overflow = previousOverflow;
      returnFocus?.focus?.();
    };
  }, [onClose, returnFocus]);

  return createPortal(
    <div className={`enterprise-document-preview${darkActive ? ' is-dark' : ''}`} onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <section role="dialog" aria-modal="true" aria-labelledby="personal-document-preview-title">
        <header>
          <div>
            <h2 id="personal-document-preview-title">{label}预览</h2>
            <span>{material.original_name || '已上传身份证照片'}</span>
          </div>
          <button ref={closeButtonRef} type="button" aria-label="关闭身份证预览" onClick={onClose}><X size={16} /></button>
        </header>
        <div className="enterprise-document-preview__body">
          {contentState === 'loading' && (
            <div className="enterprise-document-preview__state" role="status">
              <Loader2 className="canvas-vnext-spin" size={18} />
              <span>正在加载{label}…</span>
            </div>
          )}
          {contentState === 'error' && (
            <div className="enterprise-document-preview__state is-error" role="alert">
              <CircleAlert size={22} />
              <strong>照片暂时无法在线预览</strong>
              <span>临时查看授权可能已失效，请重新加载。</span>
              <button type="button" onClick={onRetry}>重新加载</button>
            </div>
          )}
          <img
            className={contentState === 'loaded' ? '' : 'is-loading'}
            src={material.preview_url}
            alt={`${label}预览`}
            referrerPolicy="no-referrer"
            onLoad={() => setContentState('loaded')}
            onError={() => setContentState('error')}
          />
        </div>
        <footer>
          <span>照片长期私有保存，本页仅使用短时授权地址预览。</span>
          {contentState !== 'error' && <button type="button" onClick={onRetry}>重新加载</button>}
        </footer>
      </section>
    </div>,
    window.document.body,
  );
}

function PersonalDocumentField({
  label,
  material,
  existing,
  uploading,
  disabled,
  onUpload,
  onPreview,
  previewing,
  previewDisabled,
}) {
  const canPreview = Boolean(material?.id);
  return (
    <article className={`enterprise-certification-upload-item${material ? ' is-uploaded' : ''}`}>
      {canPreview ? (
        <button
          type="button"
          className="enterprise-certification-upload-thumb"
          aria-label={previewing ? `正在授权查看${label}` : `放大查看${label}`}
          aria-busy={previewing}
          disabled={previewDisabled}
          onClick={event => onPreview(material, label, event.currentTarget)}
        >
          <FileImage size={22} />
          <span>{previewing ? <><Loader2 className="canvas-vnext-spin" size={11} />授权中</> : <><Maximize2 size={11} />放大</>}</span>
        </button>
      ) : (
        <div className="enterprise-certification-upload-thumb is-empty" aria-hidden="true"><UploadCloud size={20} /></div>
      )}
      <span className="enterprise-certification-upload-meta">
        <strong>{label}（必传）</strong>
        <small>{material
          ? `${existing ? '已上传，可查看或替换' : '本次已上传'} · ${material.original_name || '身份证照片'}`
          : '照片需完整、清晰、四角可见；JPG/PNG，最大 10 MB'}</small>
      </span>
      <label className="enterprise-certification-upload-action">
        <input type="file" accept="image/jpeg,image/png" aria-label={`${material ? '替换' : '上传'}${label}`} disabled={disabled} onChange={onUpload} />
        {uploading ? <Loader2 className="canvas-vnext-spin" size={13} /> : <UploadCloud size={13} />}
        <span>{uploading ? '上传中' : material ? '替换' : '上传'}</span>
      </label>
    </article>
  );
}

function ReadOnlyPersonalCertification({ certification, status, onPreview, previewingDocumentId }) {
  const front = certificationDocument(certification, 'id_card_front');
  const back = certificationDocument(certification, 'id_card_back');
  const previewBusy = Boolean(previewingDocumentId);
  const documentButton = (material, label) => material?.id ? (
    <button
      type="button"
      aria-busy={String(material.id) === previewingDocumentId}
      disabled={previewBusy}
      onClick={event => onPreview(material, label, event.currentTarget)}
    >
      {String(material.id) === previewingDocumentId ? <Loader2 className="canvas-vnext-spin" size={12} /> : <FileImage size={12} />}
      {String(material.id) === previewingDocumentId ? `正在授权查看${label}` : `查看${label}`}
    </button>
  ) : null;

  return (
    <div className="enterprise-certification-summary">
      <div className="enterprise-certification-summary__identity">
        <span><UserRound size={15} />认证个人</span>
        <strong>{certification.real_name}</strong>
        <code>{certification.id_card_number_masked || '身份证号已加密保存'}</code>
      </div>
      <dl>
        <div><dt>提交时间</dt><dd>{dateText(certification.submitted_at || certification.created_at)}</dd></div>
        <div><dt>{status === 'approved' ? '通过时间' : '审核状态'}</dt><dd>{status === 'approved' ? dateText(certification.approved_at || certification.reviewed_at) : certification.status_label}</dd></div>
      </dl>
      <div className="enterprise-certification-documents">
        {documentButton(front, '身份证正面')}
        {documentButton(back, '身份证反面')}
      </div>
    </div>
  );
}

export default function PersonalCertificationPanel({
  user,
  certification,
  permissions = {},
  reviewNotice = '',
  loading,
  error,
  onRetry,
  onChanged,
  onSubmitted,
  onEditingChange,
  preview = false,
  darkActive = false,
}) {
  const status = normalizePersonalCertificationStatus(certification);
  const canSubmit = Boolean(permissions.can_submit) && !preview;
  const [editing, setEditing] = useState(['none', 'rejected', 'revoked'].includes(status));
  const [form, setForm] = useState(() => emptyForm(certification, user));
  const [documents, setDocuments] = useState({ id_card_front: null, id_card_back: null });
  const [uploading, setUploading] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [reauthOpen, setReauthOpen] = useState(false);
  const [retryPayload, setRetryPayload] = useState(null);
  const [materialPreview, setMaterialPreview] = useState(null);
  const [previewingDocumentId, setPreviewingDocumentId] = useState('');
  const previewRequestRef = useRef(null);
  const resetDefaultsRef = useRef({ certification, user, status });
  const onEditingChangeRef = useRef(onEditingChange);
  resetDefaultsRef.current = { certification, user, status };
  onEditingChangeRef.current = onEditingChange;
  // Status polling returns fresh object references; only a real owner/certification revision may discard the in-memory draft.
  const draftOwnerIdentity = String(user?.id || user?.email || '');
  const draftContextIdentity = JSON.stringify([
    draftOwnerIdentity,
    String(certification?.id || ''),
    String(certification?.version ?? ''),
    status,
  ]);

  useEffect(() => {
    const resetDefaults = resetDefaultsRef.current;
    setForm(emptyForm(resetDefaults.certification, resetDefaults.user));
    setDocuments({ id_card_front: null, id_card_back: null });
    setEditing(['none', 'rejected', 'revoked'].includes(resetDefaults.status));
    onEditingChangeRef.current?.(false);
    setFeedback({ type: '', message: '' });
    setRetryPayload(null);
    setReauthOpen(false);
    setMaterialPreview(null);
    previewRequestRef.current = null;
    setPreviewingDocumentId('');
  }, [draftContextIdentity]);

  const updateField = event => {
    const { name, checked, type, value } = event.target;
    setForm(current => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
    setFeedback({ type: '', message: '' });
  };

  const openMaterialPreview = useCallback(async (material, label, returnFocus) => {
    if (!material?.id || previewRequestRef.current) return;
    const requestToken = Symbol('personal-document-preview');
    previewRequestRef.current = requestToken;
    setPreviewingDocumentId(String(material.id));
    setFeedback({ type: '', message: '' });
    try {
      const response = await getPersonalCertificationDocumentPreview(material.id);
      if (previewRequestRef.current !== requestToken) return;
      const freshMaterial = response?.document || response;
      if (!freshMaterial?.preview_url) throw new Error('系统未返回可用的预览地址');
      setMaterialPreview({ material: { ...material, ...freshMaterial }, label, returnFocus });
    } catch (previewError) {
      if (previewRequestRef.current !== requestToken) return;
      setFeedback({ type: 'error', message: getErrorMessage(`${label}查看失败：${previewError.message || '暂时无法授权'}，请重试`) });
    } finally {
      if (previewRequestRef.current === requestToken) {
        previewRequestRef.current = null;
        setPreviewingDocumentId('');
      }
    }
  }, []);

  const closeMaterialPreview = useCallback(() => setMaterialPreview(null), []);
  const retryMaterialPreview = useCallback(() => {
    if (!materialPreview) return;
    const { material, label, returnFocus } = materialPreview;
    setMaterialPreview(null);
    openMaterialPreview(material, label, returnFocus);
  }, [materialPreview, openMaterialPreview]);

  const uploadDocument = async (event, documentType) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const validation = documentError(file);
    if (validation) {
      setFeedback({ type: 'error', message: getErrorMessage(validation) });
      return;
    }
    setUploading(documentType);
    setFeedback({ type: '', message: '' });
    try {
      const document = await uploadPersonalCertificationDocument(file, documentType);
      setDocuments(current => ({ ...current, [documentType]: document }));
      setFeedback({ type: 'success', message: documentType === 'id_card_front' ? '身份证正面已上传' : '身份证反面已上传' });
    } catch (uploadError) {
      setFeedback({ type: 'error', message: getErrorMessage(uploadError, '照片上传失败，请重试') });
    } finally {
      setUploading('');
    }
  };

  const runSubmit = async payload => {
    setSubmitting(true);
    setFeedback({ type: '', message: '' });
    try {
      await submitPersonalCertification(payload);
      setRetryPayload(null);
      setEditing(false);
      onEditingChange?.(false);
      setForm(current => ({ ...current, id_card_number: '', information_confirmed: false }));
      await onChanged?.();
      const message = '个人认证已提交，审核通过后才可申请个人发票';
      setFeedback({ type: 'success', message });
      onSubmitted?.(message);
    } catch (submitError) {
      if (submitError.code === 'REAUTH_REQUIRED') {
        setRetryPayload(payload);
        setReauthOpen(true);
        setFeedback({ type: 'warning', message: '请先验证当前账号，验证成功后会自动继续提交' });
      } else {
        setFeedback({ type: 'error', message: getErrorMessage(submitError, '个人认证提交失败') });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submit = event => {
    event.preventDefault();
    const realName = form.real_name.trim();
    const idCardNumber = form.id_card_number.trim().toUpperCase();
    const existingFront = certificationDocument(certification, 'id_card_front');
    const existingBack = certificationDocument(certification, 'id_card_back');
    const canReuseExistingDocuments = !(status === 'approved' && editing);
    const front = documents.id_card_front || (canReuseExistingDocuments ? existingFront : null);
    const back = documents.id_card_back || (canReuseExistingDocuments ? existingBack : null);
    if (realName.length < 2) {
      setFeedback({ type: 'error', message: '请填写与身份证一致的真实姓名' });
      return;
    }
    if (!isValidIdCardNumber(idCardNumber)) {
      setFeedback({ type: 'error', message: '请输入正确的 18 位身份证号' });
      return;
    }
    if (!front?.id || !back?.id) {
      setFeedback({ type: 'error', message: '请上传清晰的身份证正面和反面照片' });
      return;
    }
    if (!form.information_confirmed) {
      setFeedback({ type: 'error', message: '请确认由本人提交真实有效的身份信息' });
      return;
    }
    runSubmit({
      real_name: realName,
      id_card_number: idCardNumber,
      id_card_front_document_id: front.id,
      id_card_back_document_id: back.id,
    });
  };

  const handleReauthenticated = async () => {
    setReauthOpen(false);
    if (retryPayload) await runSubmit(retryPayload);
  };

  if (loading) {
    return <section className="enterprise-certification-card is-loading" aria-live="polite"><Loader2 className="canvas-vnext-spin" size={17} /><span>正在核验个人认证状态</span></section>;
  }

  if (error) {
    return <section className="enterprise-certification-card is-error" role="alert"><CircleAlert size={17} /><div><strong>个人认证状态加载失败</strong><span>{error}</span></div><button type="button" onClick={onRetry}><RefreshCw size={12} />重新加载</button></section>;
  }

  const approved = status === 'approved';
  const pending = status === 'pending';
  const showForm = editing && canSubmit;
  const existingFront = certificationDocument(certification, 'id_card_front');
  const existingBack = certificationDocument(certification, 'id_card_back');
  const requireFreshApprovedDocuments = approved && editing;
  const front = documents.id_card_front || (requireFreshApprovedDocuments ? null : existingFront);
  const back = documents.id_card_back || (requireFreshApprovedDocuments ? null : existingBack);

  return (
    <section className={`enterprise-certification-card personal-certification-card is-${status}`} aria-labelledby="personal-certification-title">
      <header>
        <div className="enterprise-certification-step">1</div>
        <div>
          <h2 id="personal-certification-title">个人实名认证</h2>
          <p>真实姓名与身份证照片审核通过后，才能以个人抬头申请发票。</p>
        </div>
        <span className={`enterprise-certification-status is-${statusTone(status)}`}>
          {approved ? <CheckCircle2 size={12} /> : pending ? <Loader2 size={12} /> : <ShieldCheck size={12} />}
          {certification?.status_label || (status === 'none' ? '未提交' : status)}
        </span>
      </header>

      <p className="enterprise-certification-disclaimer">
        姓名和证件仅用于实名核验、开票及必要审计，证件照片不会公开展示。
      </p>

      {certification && !showForm && (
        <ReadOnlyPersonalCertification
          certification={certification}
          status={status}
          onPreview={openMaterialPreview}
          previewingDocumentId={previewingDocumentId}
        />
      )}

      {pending && <p className="enterprise-certification-permission" role="status"><Loader2 size={13} />{reviewNotice || '资料正在审核，审核通过前个人开票会保持禁用。'}</p>}

      {certification?.review_remark && ['rejected', 'revoked'].includes(status) && (
        <p className="enterprise-certification-review" role="alert"><CircleAlert size={13} />审核未通过：{certification.review_remark}</p>
      )}

      {!canSubmit && !approved && !pending && (
        <div className="enterprise-certification-permission" role="status">
          <LockKeyhole size={15} />
          <span>当前账号暂不能提交个人认证。</span>
        </div>
      )}

      {approved && !editing && canSubmit && (
        <div className="enterprise-certification-actions">
          <span><LockKeyhole size={13} />发票抬头使用已认证姓名；如需修改，请先更新实名资料。</span>
          <button type="button" onClick={() => { setEditing(true); onEditingChange?.(true); setFeedback({ type: 'warning', message: '更新实名资料期间，个人开票会暂停至复核通过' }); }}>更新实名资料</button>
        </div>
      )}

      {showForm && (
        <form className="enterprise-certification-form" onSubmit={submit}>
          {approved ? <p className="enterprise-certification-change-warning"><CircleAlert size={13} />请重新输入完整身份证号并上传正反面新照片。提交后个人开票将暂停，待复核通过后恢复。</p>
            : status !== 'none' && <p className="enterprise-certification-change-warning"><CircleAlert size={13} />请根据审核说明修正资料；驳回后可重新提交审核。</p>}
          <div className="enterprise-certification-fields two">
            <label>
              <span>真实姓名</span>
              <input name="real_name" value={form.real_name} maxLength={64} autoComplete="name" onChange={updateField} placeholder="请与身份证完全一致" disabled={submitting} />
            </label>
            <label>
              <span>身份证号码</span>
              <input name="id_card_number" value={form.id_card_number} maxLength={18} autoComplete="off" spellCheck="false" aria-label="身份证号码" aria-describedby="personal-id-card-hint" onChange={event => { setForm(current => ({ ...current, id_card_number: event.target.value.toUpperCase().replace(/[^0-9X]/g, '') })); setFeedback({ type: '', message: '' }); }} placeholder="18 位居民身份证号" disabled={submitting} />
              <small id="personal-id-card-hint">完整号码仅保留在当前页面内存，不写入浏览器存储；提交后只显示脱敏结果。</small>
            </label>
          </div>

          <div className="enterprise-certification-uploads">
            <PersonalDocumentField
              label="身份证正面"
              material={front}
              existing={!documents.id_card_front && Boolean(existingFront)}
              uploading={uploading === 'id_card_front'}
              disabled={Boolean(uploading) || submitting}
              onUpload={event => uploadDocument(event, 'id_card_front')}
              onPreview={openMaterialPreview}
              previewing={Boolean(front?.id) && String(front.id) === previewingDocumentId}
              previewDisabled={Boolean(previewingDocumentId)}
            />
            <PersonalDocumentField
              label="身份证反面"
              material={back}
              existing={!documents.id_card_back && Boolean(existingBack)}
              uploading={uploading === 'id_card_back'}
              disabled={Boolean(uploading) || submitting}
              onUpload={event => uploadDocument(event, 'id_card_back')}
              onPreview={openMaterialPreview}
              previewing={Boolean(back?.id) && String(back.id) === previewingDocumentId}
              previewDisabled={Boolean(previewingDocumentId)}
            />
          </div>

          <label className="enterprise-certification-confirm">
            <input name="information_confirmed" type="checkbox" checked={form.information_confirmed} onChange={updateField} />
            <span>我确认由本人提交真实、有效的身份资料，并同意平台用于实名核验和开票。</span>
          </label>

          {feedback.message && <p className={`enterprise-certification-feedback is-${feedback.type}`} role={feedback.type === 'error' ? 'alert' : 'status'}>{feedback.message}</p>}

          <footer>
            <span>证件材料不会出现在开票历史和普通下载链接中。</span>
            <div>
              {approved && <button type="button" className="is-quiet" onClick={() => { setEditing(false); onEditingChange?.(false); setDocuments({ id_card_front: null, id_card_back: null }); setForm(emptyForm(certification, user)); setFeedback({ type: '', message: '' }); }}>取消修改</button>}
              <button type="submit" disabled={submitting || Boolean(uploading)}>{submitting ? <><Loader2 className="canvas-vnext-spin" size={13} />正在提交…</> : '提交个人认证'}</button>
            </div>
          </footer>
        </form>
      )}

      {!showForm && feedback.message && <p className={`enterprise-certification-feedback is-${feedback.type}`} role={feedback.type === 'error' ? 'alert' : 'status'}>{feedback.message}</p>}

      {reauthOpen && (
        <SessionReauthDialog
          loginMethods={user?.login_methods}
          onClose={() => setReauthOpen(false)}
          onVerified={handleReauthenticated}
          darkActive={darkActive}
        />
      )}

      {materialPreview && (
        <PersonalDocumentPreview
          preview={materialPreview}
          onClose={closeMaterialPreview}
          onRetry={retryMaterialPreview}
          darkActive={darkActive || Boolean(window.document.querySelector('.dark .corporate-finance-panel'))}
        />
      )}
    </section>
  );
}
